import {
  accountSearchColumns,
  boPhanSearchColumns,
  chiPhiKhongHopLeSearchColumns,
  dotThanhToanSearchColumns,
  hanThanhToanSearchColumns,
  hopDongSearchColumns,
  khachHangSearchColumns,
  kheUocSearchColumns,
  lenhSanXuatSearchColumns,
  mauSoHoaDonSearchColumns,
  phiSearchColumns,
  QUERY_KEYS,
  thueSearchColumns,
  vatTuSearchColumns,
  vuViecSearchColumns
} from '@/constants';
import {
  AccountModel,
  BoPhan,
  type ChiPhiKhongHopLeData,
  DotThanhToan,
  HanThanhToan,
  HopDong,
  KhachHang,
  KheUoc,
  type LenhSanXuat,
  MauSoHoaDon,
  Phi,
  Tax,
  TinhChatThue,
  VatTu,
  VuViec
} from '@/types/schemas';
import { CellFieldV2 as CellField, SearchFieldV2 as SearchField } from '@/components/custom/erp';
import type { InputTableCellParams } from '@/components/custom/erp';
import type { PhieuChiCellValue } from '../../../types';
import { FormMode } from '@/types/form';

export const getTaxTableColumns = (
  tinhChatThue: TinhChatThue[],
  onCellValueChange: (rowUuid: string, field: string, newValue: PhieuChiCellValue) => void,
  formMode?: FormMode
) => [
  {
    field: 'so_ct0',
    headerName: 'Số hoá đơn',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <CellField
        name='so_ct0'
        value={params.row.so_ct0}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'so_ct0', newValue)}
      />
    )
  },
  {
    field: 'so_ct2',
    headerName: 'Ký hiệu',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <CellField
        name='so_ct2'
        value={params.row.so_ct2}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'so_ct2', newValue)}
      />
    )
  },
  {
    field: 'ngay_ct0',
    headerName: 'Ngày hoá đơn',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <CellField
        name='ngay_ct0'
        type='date'
        value={params.row.ngay_ct0}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ngay_ct0', newValue)}
      />
    )
  },

  {
    field: 'ma_thue',
    headerName: 'Mã thuế',
    width: 170,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<Tax>
        searchEndpoint={`/${QUERY_KEYS.THUE}/`}
        searchColumns={thueSearchColumns}
        columnDisplay='ma_thue'
        dialogTitle='Danh mục thuế'
        value={params.row.ma_thue_data?.ten_thue || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: Tax) => onCellValueChange(params.row.uuid, 'ma_thue_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_thue',
      resultField: 'ma_thue_data',
      detailEndpoint: QUERY_KEYS.THUE
    }
  },
  {
    field: 'ma_mau_ct',
    headerName: 'Mẫu hoá đơn',
    width: 120,
    renderCell: (params: InputTableCellParams) => {
      return (
        <SearchField<MauSoHoaDon>
          searchEndpoint={`/${QUERY_KEYS.MAU_SO_HOA_DON}/`}
          searchColumns={mauSoHoaDonSearchColumns}
          columnDisplay='ma_mau_so'
          dialogTitle='Danh mục mẫu chứng từ'
          value={params.row.ma_mau_ct_data?.ma_mau_so}
          disabled={formMode === 'view'}
          onRowSelection={(row: MauSoHoaDon) => onCellValueChange(params.row.uuid, 'ma_mau_ct_data', row)}
        />
      );
    },
    pasteConfig: {
      lookupKey: 'ma_mau_ct',
      resultField: 'ma_mau_ct_data',
      detailEndpoint: QUERY_KEYS.MAU_SO_HOA_DON
    }
  },
  {
    field: 'ma_mau_bc',
    headerName: 'Mã mẫu BC',
    width: 180,
    renderCell: (params: InputTableCellParams) => {
      return (
        <CellField
          name='ma_mau_bc'
          type='select'
          value={params.row.ma_mau_bc}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'ma_mau_bc', newValue)}
          options={[
            { value: '3', label: '3. Hóa đơn giá trị gia tăng' },
            { value: '4', label: '4. Hàng hóa, dịch vụ mua vào không có hóa đơn' },
            { value: '5', label: '5. Hóa đơn bán hàng thông thường' }
          ]}
        />
      );
    }
  },
  {
    field: 'ma_tc_thue',
    headerName: 'Mã tính chất',
    width: 180,
    renderCell: (params: InputTableCellParams) => {
      const options = tinhChatThue?.map(item => ({
        value: item.uuid,
        label: item.ten_tc_thue
      }));
      return (
        <CellField
          name='ma_tc_thue'
          type='select'
          value={params.row.ma_tc_thue}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'ma_tc_thue', newValue)}
          options={options}
        />
      );
    }
  },
  {
    field: 'ma_kh',
    headerName: 'Mã ncc',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<KhachHang>
        searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}/`}
        searchColumns={khachHangSearchColumns}
        dialogTitle='Danh mục khách hàng'
        columnDisplay='customer_code'
        value={params.row.ma_kh_data?.customer_code || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: KhachHang) => onCellValueChange(params.row.uuid, 'ma_kh_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_kh',
      resultField: 'ma_kh_data',
      detailEndpoint: QUERY_KEYS.KHACH_HANG
    }
  },
  {
    field: 'ten_kh_thue',
    headerName: 'Tên nhà cung cấp',
    width: 200,
    renderCell: (params: InputTableCellParams) => (
      <CellField
        name='ten_kh_thue'
        value={params.row.ma_kh_data?.customer_name || params.row.ten_kh_thue}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ten_kh_thue', newValue)}
      />
    )
  },
  {
    field: 'dia_chi',
    headerName: 'Địa chỉ',
    width: 200,
    renderCell: (params: InputTableCellParams) => (
      <CellField
        name='dia_chi'
        value={params.row.dia_chi || params.row.ma_kh_data?.address}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'dia_chi', newValue)}
      />
    )
  },
  {
    field: 'ma_so_thue',
    headerName: 'Mã số thuế',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <CellField
        name='ma_so_thue'
        value={params.row.ma_so_thue}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ma_so_thue', newValue)}
      />
    )
  },
  {
    field: 'ten_vt_thue',
    headerName: 'Tên hàng hoá - dịch vụ',
    width: 200,
    renderCell: (params: InputTableCellParams) => (
      <CellField
        name='ten_vt_thue'
        value={params.row.ten_vt_thue}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ten_vt_thue', newValue)}
      />
    )
  },
  {
    field: 't_tien_nt',
    headerName: 'Tiền hàng VND',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <CellField
        name='t_tien_nt'
        type='number'
        value={params.row.t_tien_nt}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 't_tien_nt', newValue)}
      />
    )
  },
  {
    field: 'tk_thue_no',
    headerName: 'TK thuế',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<AccountModel>
        searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
        searchColumns={accountSearchColumns}
        dialogTitle='Danh mục tài khoản'
        columnDisplay='code'
        value={params.row.tk_thue_no_data?.code || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: AccountModel) => onCellValueChange(params.row.uuid, 'tk_thue_no_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'tk_thue_no',
      resultField: 'tk_thue_no_data',
      detailEndpoint: QUERY_KEYS.TAI_KHOAN
    }
  },
  {
    field: 'tk_du',
    headerName: 'TK đối ứng',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<AccountModel>
        searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
        searchColumns={accountSearchColumns}
        dialogTitle='Danh mục tài khoản'
        columnDisplay='code'
        value={params.row.tk_du_data?.code || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: AccountModel) => onCellValueChange(params.row.uuid, 'tk_du_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'tk_du',
      resultField: 'tk_du_data',
      detailEndpoint: QUERY_KEYS.TAI_KHOAN
    }
  },
  {
    field: 't_thue_nt',
    headerName: 'Thuế VND',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <CellField
        name='t_thue_nt'
        type='number'
        value={params.row.t_thue_nt}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 't_thue_nt', newValue)}
      />
    )
  },
  {
    field: 'ma_kh9',
    headerName: 'Cục thuế',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<KhachHang>
        searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}/`}
        searchColumns={khachHangSearchColumns}
        dialogTitle='Danh mục đối tượng'
        columnDisplay='customer_code'
        value={params.row.ma_kh9_data?.customer_code || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: KhachHang) => onCellValueChange(params.row.uuid, 'ma_kh9_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_kh9',
      resultField: 'ma_kh9_data',
      detailEndpoint: QUERY_KEYS.KHACH_HANG
    }
  },
  {
    field: 'ma_tt',
    headerName: 'Mã thanh toán',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<HanThanhToan>
        searchEndpoint={`/${QUERY_KEYS.HAN_THANH_TOAN}/`}
        searchColumns={hanThanhToanSearchColumns}
        dialogTitle='Danh mục thanh toán'
        columnDisplay='ma_tt'
        value={params.row.ma_tt_data?.ma_tt || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: HanThanhToan) => onCellValueChange(params.row.uuid, 'ma_tt_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_tt',
      resultField: 'ma_tt_data',
      detailEndpoint: QUERY_KEYS.HAN_THANH_TOAN
    }
  },
  {
    field: 'ghi_chu',
    headerName: 'Ghi chú',
    width: 200,
    renderCell: (params: InputTableCellParams) => (
      <CellField
        name='ghi_chu'
        value={params.row.ghi_chu}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ghi_chu', newValue)}
      />
    )
  },
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<BoPhan>
        searchEndpoint={`/${QUERY_KEYS.BO_PHAN}/`}
        searchColumns={boPhanSearchColumns}
        dialogTitle='Danh mục bộ phận'
        columnDisplay='ma_bp'
        value={params.row.ma_bp_data?.ma_bp || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: BoPhan) => onCellValueChange(params.row.uuid, 'ma_bp_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_bp',
      resultField: 'ma_bp_data',
      detailEndpoint: QUERY_KEYS.BO_PHAN
    }
  },
  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<VuViec>
        searchEndpoint={`/${QUERY_KEYS.VU_VIEC}/`}
        searchColumns={vuViecSearchColumns}
        dialogTitle='Danh mục vụ việc'
        columnDisplay='ma_vu_viec'
        value={params.row.ma_vv_data?.ma_vu_viec || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: VuViec) => onCellValueChange(params.row.uuid, 'ma_vv_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_vv',
      resultField: 'ma_vv_data',
      detailEndpoint: QUERY_KEYS.VU_VIEC
    }
  },
  {
    field: 'ma_hd',
    headerName: 'Mã hợp đồng',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<HopDong>
        searchEndpoint={`/${QUERY_KEYS.HOP_DONG}/`}
        searchColumns={hopDongSearchColumns}
        dialogTitle='Danh mục hợp đồng'
        columnDisplay='ma_hd'
        value={params.row.ma_hd_data?.ma_hd || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: HopDong) => onCellValueChange(params.row.uuid, 'ma_hd_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_hd',
      resultField: 'ma_hd_data',
      detailEndpoint: QUERY_KEYS.HOP_DONG
    }
  },
  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<DotThanhToan>
        searchEndpoint={`/${QUERY_KEYS.DOT_THANH_TOAN}/`}
        searchColumns={dotThanhToanSearchColumns}
        dialogTitle='Danh mục đợt thanh toán'
        columnDisplay='ma_dtt'
        value={params.row.ma_dtt_data?.ma_dtt || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: DotThanhToan) => onCellValueChange(params.row.uuid, 'ma_dtt_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_dtt',
      resultField: 'ma_dtt_data',
      detailEndpoint: QUERY_KEYS.DOT_THANH_TOAN
    }
  },
  {
    field: 'ma_ku',
    headerName: 'Khế ước',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<KheUoc>
        searchEndpoint={`/${QUERY_KEYS.KHE_UOC}/`}
        searchColumns={kheUocSearchColumns}
        dialogTitle='Danh mục khế ước'
        columnDisplay='ma_ku'
        value={params.row.ma_ku_data?.ma_ku || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: KheUoc) => onCellValueChange(params.row.uuid, 'ma_ku_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_ku',
      resultField: 'ma_ku_data',
      detailEndpoint: QUERY_KEYS.KHE_UOC
    }
  },
  {
    field: 'ma_phi',
    headerName: 'Phí',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<Phi>
        searchEndpoint={`/${QUERY_KEYS.PHI}/`}
        searchColumns={phiSearchColumns}
        dialogTitle='Danh mục phí'
        columnDisplay='ma_phi'
        value={params.row.ma_phi_data?.ma_phi || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: Phi) => onCellValueChange(params.row.uuid, 'ma_phi_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_phi',
      resultField: 'ma_phi_data',
      detailEndpoint: QUERY_KEYS.PHI
    }
  },
  {
    field: 'ma_sp',
    headerName: 'Sản phẩm',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<VatTu>
        searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
        searchColumns={vatTuSearchColumns}
        dialogTitle='Danh mục sản phẩm'
        columnDisplay='ma_vt'
        value={params.row.ma_sp_data?.ma_vt || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: VatTu) => onCellValueChange(params.row.uuid, 'ma_sp_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_sp',
      resultField: 'ma_sp_data',
      detailEndpoint: QUERY_KEYS.VAT_TU
    }
  },
  {
    field: 'ma_lsx',
    headerName: 'Lệnh sản xuất',
    width: 150,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<LenhSanXuat>
        searchEndpoint={`/${QUERY_KEYS.LENH_SAN_XUAT}/`}
        searchColumns={lenhSanXuatSearchColumns}
        dialogTitle='Danh mục lệnh sản xuất'
        columnDisplay='so_ct'
        value={params.row.ma_lsx_data?.so_ct || params.row.ma_lsx || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: LenhSanXuat) => onCellValueChange(params.row.uuid, 'ma_lsx_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_lsx',
      resultField: 'ma_lsx_data',
      detailEndpoint: QUERY_KEYS.LENH_SAN_XUAT
    }
  },
  {
    field: 'ma_cp0',
    headerName: 'C/p không h/lệ',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<ChiPhiKhongHopLeData>
        searchEndpoint={`/${QUERY_KEYS.CHI_PHI_KHONG_HOP_LE}/`}
        searchColumns={chiPhiKhongHopLeSearchColumns}
        dialogTitle='Danh mục chi phí không hợp lệ'
        columnDisplay='ma_cp_khl'
        value={params.row.ma_cp0_data?.ma_cpkhl || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: ChiPhiKhongHopLeData) => onCellValueChange(params.row.uuid, 'ma_cp0_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_cp0',
      resultField: 'ma_cp0_data',
      detailEndpoint: QUERY_KEYS.CHI_PHI_KHONG_HOP_LE
    }
  }
];
